import addMediaImg from '@assets/workspace-signposts/add-media.jpg';
import askAgentImg from '@assets/workspace-signposts/ask-ai-agents.jpg';
import demoLibraryImg from '@assets/workspace-signposts/demo-library.jpg';
import podcastImg from '@assets/workspace-signposts/podcast.jpg';
import { PLATFORMS } from '@lib/utils/platforms';
import { VERBS } from '@lib/verbs';
import AddLinkRoundedIcon from '@mui/icons-material/AddLinkRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import classNames from 'classnames';
import { useCycleIndex } from '../useCycleIndex';
import css from './CapabilityPreviews.module.scss';

const CYCLE_MS = 3200;

// Things that are actually in `demo-library.jpg`, so the query matches the wall behind it.
const SEARCH_QUERIES = ['someone by the sea', 'a crescent moon', 'waves at sunset'];

const AGENT_EXCHANGES = [
  { ask: '3 vertical clips about pricing', result: '3 clips · 9:16 · captioned' },
  { ask: 'Cut the interview to a minute', result: '1 edit · 0:58 · ready to post' },
  { ask: 'Find every shot of our logo', result: '14 moments found' },
];

const IMPORT_SOURCES = [PLATFORMS.youtube, PLATFORMS.googledrive, PLATFORMS.dropbox];

/** Stacks every variant in one grid cell and fades between them, so the box never resizes. */
const CyclingText = ({ items, index }: { items: string[]; index: number }) => (
  <span className={css.cycle}>
    {items.map((item, i) => (
      <span key={item} className={classNames(css.cycleItem, { [css.cycleActive]: i === index })}>
        {item}
      </span>
    ))}
  </span>
);

export const PodcastPreview = () => (
  <div className={css.podcastStage}>
    <img
      src={podcastImg}
      className={classNames(css.backdrop, css.dim, css.dimLight, css.podcastFrame)}
      alt=""
    />
  </div>
);

export const SearchPreview = () => {
  const index = useCycleIndex(SEARCH_QUERIES.length, CYCLE_MS);
  return (
    <div className={css.stage}>
      <img src={demoLibraryImg} className={classNames(css.backdrop, css.dim)} alt="" />
      <div className={css.searchPill}>
        <span className={css.searchGlyph}>{VERBS['find-moments'].icon}</span>
        <CyclingText items={SEARCH_QUERIES} index={index} />
        <span className={css.caret} />
      </div>
    </div>
  );
};

export const AgentPreview = () => {
  const index = useCycleIndex(AGENT_EXCHANGES.length, CYCLE_MS);
  return (
    <div className={css.stage}>
      <img src={askAgentImg} className={classNames(css.backdrop, css.dim)} alt="" />
      <div className={css.chat}>
        <span className={classNames(css.bubble, css.bubbleUser)}>
          <CyclingText items={AGENT_EXCHANGES.map((e) => e.ask)} index={index} />
        </span>
        <span className={classNames(css.bubble, css.bubbleAgent)}>
          <span className={css.agentGlyph}>{VERBS['work-with-agent'].icon}</span>
          <CyclingText items={AGENT_EXCHANGES.map((e) => e.result)} index={index} />
          <CheckRoundedIcon className={css.check} />
        </span>
      </div>
    </div>
  );
};

export const AddMediaPreview = () => (
  <div className={css.stage}>
    <img src={addMediaImg} className={classNames(css.backdrop, css.dim, css.dimSoft)} alt="" />
    <div className={css.sources}>
      <span className={css.source}>
        <FileUploadOutlinedIcon />
        Upload
      </span>
      {IMPORT_SOURCES.map((platform) => (
        <span key={platform.key} className={css.source}>
          {platform.icon && <img src={platform.icon} alt="" />}
          {platform.displayName}
        </span>
      ))}
      <span className={css.source}>
        <AddLinkRoundedIcon />
        Any link
      </span>
    </div>
  </div>
);
